// Settings dirty-state tracking, moved out of app.js unchanged (10/19, the
// standing rule: extract from app.js what you touch, the move and the change in
// separate commits). app.js still exposes it as window.initSettingsDirtyTracking,
// which modules/settings.js calls after renderSettings().
//
// ── Settings dirty-state tracking ─────────────────────────────
// Called by renderSettings() after fields are populated.
// Snapshots current values → disables each save button → re-enables on change.
export function initSettingsDirtyTracking() {
  const groups = [
    ['btn-save-currency',     ['s-currency']],
    ['btn-save-distance-unit', ['s-distance-unit']],
    ['btn-save-date-format',  ['s-date-format']],
    ['btn-save-intensity-metric', ['s-intensity-metric']],
    // Settings → Check-Ins → Your voice (2026-10-01): the brief's draft reply follows these.
    ['btn-save-voice', ['s-voice-profile', 's-voice-no-dashes', 's-voice-no-exclamation', 's-voice-signoff', 's-voice-avoid', 's-voice-max-words', 's-voice-samples']],
    // ⚠️ Both of these called _dirtyReset() from their save handlers but were
    // never REGISTERED here, so the optional-chained call hit undefined and
    // the button never greyed out — leaving a coach unable to tell whether
    // their change had saved. Reported 2026-08-12 (the alert-phone field made
    // it obvious, but Coach Profile and Form Check Videos were both already
    // like this).
    // ⚠️ Registering the input here is the OTHER half of adding a field to
    // this card — the save handler reads the DOM, but the button only lights
    // up for inputs in this list. s-coach-name shipped without it (2026-08-13)
    // and the name field looked wired while Save stayed grey.
    ['btn-save-ig-handle',   ['s-coach-name', 's-business-name', 's-ig-handle', 's-alert-phone', 's-credential', 's-role', 's-business-address']],
    ['btn-save-form-video',  ['s-form-video-mode', 's-form-video-cap']],
    ['btn-save-timezone',    ['s-timezone']],
    ['btn-save-email',       ['s-email-business-name', 's-email-reply-to', 's-email-send-receipts', 's-email-send-failure']],
    ['btn-save-sessions',          ['s-client-cancel-extra-days', 's-coach-cancel-extra-days',
      's-forgive-extra-days']],
    ['btn-save-messaging',         ['s-one-way-messaging']],
    // ⚠️ s-tab-workouts removed 2026-08-12: no such input exists in the page
    // (there are seven client-tab toggles, not eight). A registry entry naming
    // a missing id silently contributes a constant to the snapshot, so the
    // button could never be dirtied by it — harmless here, but the same
    // mistake on a real field would make a button grey out and stay that way.
    ['btn-save-client-tabs',       ['s-tab-calendar', 's-tab-nutrition', 's-tab-checkins', 's-tab-sessions', 's-tab-courses', 's-tab-community', 's-tab-progress', 's-client-teasers']],
    // Registered 2026-08-12 alongside Coach Profile and Form Check Videos —
    // none of these greyed out either, so every Settings card now behaves the
    // same way. settings-dirty-tracking-coverage.test.mjs keeps it that way.
    // s-sched-blackout was the missing one here — readFromFields() in
    // modules/scheduling-settings.js parses it on save, but editing only
    // blackout dates never lit the button (2026-08-13 audit).
    // s-sched-earliest-hours and s-sched-latest-days left with the weekly grid (10/16): how soon and how far ahead
    // belong to the set of hours now; s-sched-hours-set is the picker of that set.
    ['btn-save-scheduling',        ['s-sched-enabled', 's-sched-approval', 's-sched-session-min', 's-sched-buffer-min', 's-sched-notice-hours', 's-sched-hours-set', 's-sched-max-per-day', 's-sched-location-label', 's-sched-location-address', 's-sched-home-address', 's-sched-day-starts-from', 's-sched-travel-time', 's-sched-booking-message']],
    ['btn-save-nudge',             ['s-nudge-enabled', 's-nudge-days']],
    // The 2026-08-13 audit found this card the worst offender: the handler
    // reads six inputs and the registry listed ONE, so editing pause caps,
    // churn settings, or the cancellation agreement never enabled Save.
    // (Its custom dropdown s-retention-default-approach is a dataset-value DIV
    // this mechanism cannot snapshot; it stays untracked. The churn switch and
    // alert preference left the page on 10/20.)
    ['btn-save-retention',         ['s-retention-followups-enabled', 's-retention-max-pause-days', 's-retention-max-pauses-year', 's-churn-booking-link', 's-cancel-agreement-template']],
    // ⚠️ The two hidden inputs carry the mode and protein-limit BUTTONS (10/20): buttons alone never turned Save on,
    // so a coach switching to "Specific kcal / g" (showing 100 / 10, his numbers) could never save it.
    ['btn-save-nutrition-tolerance', ['nut-tol-mode-value', 'nut-tol-percent', 'nut-tol-abs-calories', 'nut-tol-abs-protein', 'nut-tol-abs-carbs', 'nut-tol-abs-fat', 'nut-tol-protein-limit']],
    ['btn-save-freeze-config',     ['freeze-refill-count', 'freeze-max-accumulation']],
    // Receipt-source radio is excluded on purpose (it saves immediately). Logo
    // is a hidden input set via JS — setReceiptLogo() dispatches 'input' so this
    // still fires. (receipt-delivery radio saves separately, not here.)
    ['btn-save-receipt-brand',     ['s-receipt-logo-url', 's-receipt-accent-color', 's-receipt-support-email', 's-receipt-footer-text']],
  ];
  groups.forEach(([btnId, fieldIds]) => {
    const btn = document.getElementById(btnId);
    if (!btn) return;
    const snap = () => fieldIds.map(id => { const el = document.getElementById(id); if (!el) return ''; return el.type === 'checkbox' ? String(el.checked) : el.value; }).join('\0');
    // data-always-save="1": a card with nothing saved yet keeps Save on, so its shown values can be saved as they are.
    btn._dirtyReset = () => { btn._cleanSnap = snap(); btn.disabled = btn.dataset.alwaysSave !== '1'; };
    btn._dirtyReset();
    if (!btn._dirtyBound) {
      btn._dirtyBound = true;
      fieldIds.forEach(id => {
        const el = document.getElementById(id);
        if (!el) return;
        ['input', 'change'].forEach(evt =>
          el.addEventListener(evt, () => { btn.disabled = btn.dataset.alwaysSave !== '1' && snap() === btn._cleanSnap; })
        );
      });
    }
  });
}
